import { useEffect, useState, useCallback } from "react";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import api from "@/lib/api";

/**
 * FounderSpotlight — rotating carousel of curated founder testimonials.
 * Auto-advances every 7s (pauseable on hover). Manual prev/next + dot nav.
 * Uses initials-avatar (colored circle) until real photos are supplied — see
 * backend/app/routers/launch.py `_FOUNDER_SPOTLIGHTS` seed to add photo URLs.
 */
const ACCENT_MAP = {
  kraft: "var(--ff-accent-kraft)",
  citrus: "var(--ff-citrus)",
  forest: "var(--ff-primary)",
};

function initialsOf(name) {
  if (!name) return "•";
  return name.trim().charAt(0).toUpperCase();
}

export default function FounderSpotlight() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/launch/founder-spotlights?limit=6")
      .then(r => { if (!cancelled) setItems(r.data?.spotlights || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const next = useCallback(() => {
    setIdx(i => (items.length ? (i + 1) % items.length : 0));
  }, [items.length]);

  const prev = useCallback(() => {
    setIdx(i => (items.length ? (i - 1 + items.length) % items.length : 0));
  }, [items.length]);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const t = setInterval(next, 7000);
    return () => clearInterval(t);
  }, [paused, items.length, next]);

  if (items.length === 0) return null;

  const s = items[idx];
  const bg = ACCENT_MAP[s.accent] || ACCENT_MAP.forest;
  const initials = initialsOf(s.first_name);

  return (
    <section
      className="max-w-5xl mx-auto px-6 py-20"
      data-testid="founder-spotlight-section"
      aria-labelledby="founder-spotlight-heading"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="text-center mb-10">
        <p className="overline text-[var(--ff-primary)] inline-flex items-center gap-1.5 justify-center">
          <Quote size={12}/> Founder spotlight
        </p>
        <h2
          id="founder-spotlight-heading"
          className="font-display text-3xl sm:text-4xl mt-3 text-[var(--ff-primary)] leading-tight"
        >
          Why they claimed a seat.
        </h2>
      </div>

      <div
        className="relative rounded-3xl bg-white border border-[var(--ff-border)] shadow-[0_20px_60px_-30px_rgba(26,67,49,0.25)] px-8 sm:px-14 py-10 sm:py-14"
        data-testid="founder-spotlight-card"
      >
        {/* Avatar + copy */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 sm:gap-8">
          <div
            className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center font-display text-3xl sm:text-4xl text-white shadow-md"
            style={{ background: bg }}
            aria-hidden="true"
            data-testid="founder-spotlight-avatar"
          >
            {initials}
          </div>
          <div className="flex-1 text-center sm:text-left">
            <p
              className="font-display text-xl sm:text-2xl leading-snug text-[var(--ff-primary)]"
              data-testid="founder-spotlight-quote"
            >
              &ldquo;{s.quote}&rdquo;
            </p>
            <p className="mt-5 text-sm text-[var(--ff-text-secondary)]">
              <span className="font-semibold text-[var(--ff-text-primary)]" data-testid="founder-spotlight-name">
                {s.first_name}
              </span>
              <span className="mx-2 text-[var(--ff-text-muted)]">·</span>
              <span data-testid="founder-spotlight-city">{s.city}</span>
              <span className="mx-2 text-[var(--ff-text-muted)]">·</span>
              <span
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                style={{ background: "var(--ff-accent-kraft)", color: "var(--ff-primary)" }}
                data-testid="founder-spotlight-badge"
              >
                Founder #{s.founder_number} · {s.protein}
              </span>
            </p>
          </div>
        </div>

        {/* Prev / Next */}
        {items.length > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Previous founder"
              data-testid="founder-spotlight-prev"
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[var(--ff-border)] shadow-sm flex items-center justify-center text-[var(--ff-primary)] hover:bg-[var(--ff-accent-kraft)] transition"
            >
              <ChevronLeft size={18}/>
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next founder"
              data-testid="founder-spotlight-next"
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-[var(--ff-border)] shadow-sm flex items-center justify-center text-[var(--ff-primary)] hover:bg-[var(--ff-accent-kraft)] transition"
            >
              <ChevronRight size={18}/>
            </button>
          </>
        )}
      </div>

      {/* Dots */}
      {items.length > 1 && (
        <div className="mt-6 flex justify-center gap-2" data-testid="founder-spotlight-dots">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              aria-label={`Show founder ${i + 1} of ${items.length}`}
              aria-current={i === idx}
              data-testid={`founder-spotlight-dot-${i}`}
              className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-[var(--ff-primary)]" : "w-2 bg-[var(--ff-border)] hover:bg-[var(--ff-text-muted)]"}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
